import { useEffect } from 'react'
import { useOrderStore, getSupabaseClient, driverFromJson } from '@restaurant/core'
import type { OrderState } from '@restaurant/core'
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { isShowcase } from '../showcase/showcase'

/** Minimal shape of the realtime `orders` row we read in the change handler. */
interface OrderRealtimeRecord {
  order_id?: string | number
  status?: OrderState
}

/** Minimal shape of the realtime `drivers` row we read in the change handler. */
interface DriverRealtimeRecord {
  order_id?: string | number
}

/**
 * App-level hook that manages Supabase Realtime subscriptions for active order tracking.
 * Subscribes to `orders` and `drivers` table changes when an active order exists.
 * Must be mounted in App.tsx alongside useOrderHistoryData.
 */
export function useActiveOrderTracking() {
  const activeOrder = useOrderStore((s) => s.activeOrder)
  const setActiveOrderTrackingStatus = useOrderStore((s) => s.setActiveOrderTrackingStatus)
  const setActiveOrderDriverLocation = useOrderStore((s) => s.setActiveOrderDriverLocation)
  const setTrackingError = useOrderStore((s) => s.setTrackingError)

  useEffect(() => {
    if (isShowcase()) return
    if (!activeOrder?.id) return

    const supabase = getSupabaseClient()
    const orderId = activeOrder.id

    // Subscribe to orders table changes
    const ordersChannel = supabase
      .channel(`order-tracking-${orderId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders', filter: `order_id=eq.${orderId}` },
        (payload: RealtimePostgresChangesPayload<OrderRealtimeRecord>) => {
          const record = payload.new as OrderRealtimeRecord
          if (!record || String(record.order_id) !== orderId) return

          const status = record.status
          if (status) {
            setTrackingError(null) // Clear any stale connection errors
            setActiveOrderTrackingStatus({
              orderId,
              currentState: status,
              estimatedCompletionTime: undefined,
              actualCompletionTime: undefined,
              statusUpdates: [],
            })
          }
        }
      )
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR' || err) {
          setTrackingError('Order tracking connection lost')
        }
      })

    // Subscribe to drivers table changes (delivery orders only)
    let driversChannel: ReturnType<typeof supabase.channel> | null = null
    if (activeOrder.isDelivery) {
      driversChannel = supabase
        .channel(`driver-tracking-${orderId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'drivers', filter: `order_id=eq.${orderId}` },
          (payload: RealtimePostgresChangesPayload<DriverRealtimeRecord>) => {
            const record = payload.new as DriverRealtimeRecord
            if (!record || String(record.order_id) !== orderId) return

            const driver = driverFromJson(record)
            if (driver.latitude != null && driver.longitude != null) {
              setActiveOrderDriverLocation({
                latitude: driver.latitude,
                longitude: driver.longitude,
                timestamp: new Date().toISOString(),
              })
            }
          }
        )
        .subscribe((status, err) => {
          if (status === 'CHANNEL_ERROR' || err) {
            setTrackingError('Driver tracking connection lost')
          }
        })
    }

    return () => {
      supabase.removeChannel(ordersChannel)
      if (driversChannel) supabase.removeChannel(driversChannel)
    }
  }, [activeOrder?.id, activeOrder?.isDelivery, setActiveOrderTrackingStatus, setActiveOrderDriverLocation, setTrackingError])
}
